<template>
  <div v-if="!isDismissed" class="ak-tag" :class="classes">
    <ak-icon v-if="icon" :type="icon" class="ak-tag__icon" />
    <slot />
    <div v-if="dismissible" class="ak-tag__dismiss" @click="dismiss">
      <ak-icon type="close" />
    </div>
  </div>
</template>

<script>
import { ref, computed, defineComponent } from 'vue'
import Icon from '../icon/Icon.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-tag color="info">
   *   Content
   * </ak-tag>
   */
  name: 'AkTag',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Icon.name]: Icon,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {String}
     * @description Determines the color of the label.
     */
    color: {
      validator: (v) =>
        [
          'primary',
          'secondary',
          'tertiary',
          'quaternary',
          'grey',
          'white',
          'success',
          'danger',
          'warning',
          // 'info',
          'score-cold',
          'score-tepid',
          'score-warm',
          'score-hot',
        ].indexOf(v) >= 0,
      type: String,
      default: 'grey',
    },
    /**
     * @type {Boolean}
     * @description Change to ghost /outline styled
     * */
    ghost: {
      type: Boolean,
      default: false,
    },
    /**
     * @type {Boolean}
     * @description Change to badge styled
     * */
    badge: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {String}
     * @description Icon to display to the left of the tag
     * */
    icon: {
      type: String,
      default: '',
    },

    /**
     * @type {Boolean}
     * @description Enable the dismissing of the tag
     * */
    dismissible: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Do not wrap tag text
     * */
    nowrap: {
      type: Boolean,
      default: false,
    },

    /**
     * @type {Boolean}
     * @description Determines the size of the label
     * */
    size: {
      type: String,
      default: '',
    },
  },

  emits: ['tagDismissed'],

  /**
   * @param {Object} props - The properties of the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const isDismissed = ref(false)

    const classes = computed(() => {
      return {
        'ak-tag--has-action': typeof attrs.onClick === 'function',
        [`ak-tag--${props.color}`]: props.color,
        'ak-tag--badge': props.badge,
        'ak-tag--ghost': props.ghost,
        ['ak-tag--nowrap']: props.nowrap,
        [`ak-tag--${props.size}`]: props.size,
      }
    })

    const dismiss = () => {
      isDismissed.value = true
      emit('tagDismissed')
    }

    return {
      isDismissed,
      classes,
      dismiss,
    }
  },
})
</script>

<style lang="scss" rel="stylesheet/scss">
@import '../../scss/_settings.scss';

$colors-list: (
  (primary, $color-primary, white),
  (secondary, $color-secondary, white),
  (tertiary, $color-tertiary, white),
  (quaternary, $color-quaternary, $color-grey-dark),
  (grey, $color-grey-light, white),
  (white, $color-grey-lighter, $color-grey),
  (success, $color-success, white),
  (danger, $color-danger, white),
  (warning, $color-warning, white),
  (info, $color-info, $color-grey)
);
.ak-tag {
  $self: &;
  display: inline-flex;
  align-items: center;
  padding: 0.25em 0.825em;
  border-radius: $base-border-radius;
  font-size: 0.825em;
  font-weight: bold;
  vertical-align: middle;
  overflow-wrap: break-word;
  word-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
  border: 1px solid transparent;

  &__icon {
    margin-right: $spacing-tiny;
  }

  &__dismiss {
    border-radius: 50%;
    margin-top: -0.25em;
    margin-bottom: -0.25em;
    margin-right: -0.5em;
    margin-left: 0.25em;
    width: 1.5em;
    height: 1.5em;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    &:hover {
      background-color: rgba(0, 0, 0, 0.15);
    }
  }

  //TODO: Remove this when we change icons to svg
  .ak-icon,
  .icon {
    margin-top: 0.15em;
  }

  @each $label, $color, $badgeColor in $colors-list {
    &--#{$label} {
      background-color: lighten($color, 30%);
      color: darken($color, 35%);
      border-color: lighten($color, 30%);
      &#{$self}--ghost {
        color: $color;
        border-color: $color;
      }

      &#{$self}--badge {
        background-color: $color;
        color: $badgeColor;
        border-color: $color;
        &#{$self}--ghost {
          background-color: transparent;
          color: $color;
        }
      }
    }
  }

  &--ghost {
    background-color: transparent;
  }

  &--badge {
    border-radius: 20px;
  }

  &--has-action {
    cursor: pointer;
    transition: $transition;
    &:hover {
      opacity: 0.8;
    }
  }

  &--nowrap {
    white-space: nowrap;
  }

  &--tiny {
    font-size: 60%;
    line-height: 95%;
  }
  &--small {
    font-size: 80%;
    line-height: 95%;
  }
}
</style>
